import React, { useEffect, useState } from 'react';
import { Icon } from '../../../../../concepts/resources/Icon';
import { Button, Colors, Input } from '../../../../../ui';
import { ResultInfoForMutation } from '../../../../../ui/ResultInfo';
import { AppNavigator } from '../../../../AppNavigator';
import { useResolve } from '../../../../Container';
import { Copy, Flexer, Title } from '../../../../dashboard/elements/LayoutElements';
import { useSingleSignOnConstants, useSingleSignOnForm, useSingleSignOnMutation } from '../SingleSignOnHooks';
import { CommonFinalStep } from './CommonFinalStep';
import { InputCopyButton } from './InputCopyButton';
import { SetupStep, StepProps } from './SetupStep';
import { SetupStepViewer } from './SetupStepViewer';

const CreateAppStep: React.FC<StepProps> = ({ gotoStep }) => {
    return (
        <SetupStep title="Step 1 — Create Okta App Integration">
            <Copy flush>
                <p>From within the Okta dashboard:</p>
                <ul>
                    <li>Choose <b>Applications &gt; Create App Integration</b></li>
                    <li>Select <b>SAML 2.0</b> as the sign-in method</li>
                    <li>Click <b>Next</b></li>
                </ul>
            </Copy>
            <Copy flush>
                <p>On the General Settings page:</p>
                <ul>
                    <li>App name: <b>Metro Retro</b></li>
                    <li>App logo: Recommended to use <b>Logo with Name</b> from our <a target="_blank" href="https://metroretro.io/media" rel="noreferrer">Media Library</a></li>
                    <li>Configure other settings as desired.</li>
                    <li>Click <b>Next</b>.</li>
                </ul>
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(1)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

const ConfigureServiceProviderStep: React.FC<StepProps> = ({ gotoStep }) => {
    const { entityId, acsUrl } = useSingleSignOnConstants();

    return (
        <SetupStep title="Step 2 — Service Provider Details for Okta"> {/* Title updated */}
            <Copy flush>
                <p>
                    Under <b>SAML Settings</b>, import the settings below into your Okta App configuration:
                </p>
            </Copy>
            <Flexer>
                <Input
                    label="Single sign-on URL:"
                    value={acsUrl}
                    after={() => <InputCopyButton value={acsUrl} />}
                    hint="The endpoint your Okta IDP sends users to when they log in."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Audience URI (SP Entity ID):"
                    value={entityId}
                    after={() => <InputCopyButton value={entityId} />}
                    hint="This identifies your Metro Retro account to Okta."
                />
            </Flexer>
            <Copy flush>
                <p>Leave other settings like Default RelayState, Name ID format, Application username as their defaults unless you have specific requirements.</p>
            </Copy>
            <Copy flush>
                <p>Under <b>Attribute Statements</b> add the following mappings:</p>
                <ul>
                    <li>firstName (basic) <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> user.firstName</li>
                    <li>lastName (basic) <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> user.lastName</li>
                    <li>email (basic) <Icon size="1.25" src="deqo://icons/hero/arrow-right" /> user.email</li>
                </ul>
            </Copy>
            <Copy flush>
                <p>Click <b>Next</b> in Okta.</p>
            </Copy>
            <Flexer flex="row" justify="end">
                <Button variant="primary" onAction={() => gotoStep(2)}>
                    Next
                </Button>
            </Flexer>
        </SetupStep>
    );
};

// Step 3 - Configure Okta Identity Provider details
const ConfigureIdentityProviderStep: React.FC<StepProps> = ({ form, mutation, gotoStep }) => {
    const { draft, draftDirty, patchDraft } = form;

    useEffect(() => {
        mutation.reset();
    }, [draft]);

    const onSave = () => {
        mutation.mutate(draft);
    };

    return (
        <SetupStep title="Step 3 — Okta Identity Provider Details"> {/* Title updated */}
            <Copy flush>
                <p>
                    Finish creating your App for Metro Retro in Okta.
                    If not already on the screen, navigate to the  SAML Setup Instructions for your new app.
                    Copy the appropriate values into the inputs below:
                </p>
            </Copy>
            <Flexer>
                <Input
                    label="Identity Provider Single Sign-On URL:"
                    value={draft.entryPoint}
                    error={form.errors.entryPoint}
                    onChange={x => patchDraft({ entryPoint: x })}
                    hint="The endpoint Metro Retro sends users to authenticate (Okta's SSO URL)."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="Identity Provider Issuer:"
                    value={draft.issuer}
                    error={form.errors.issuer}
                    onChange={x => patchDraft({ issuer: x })}
                    hint="This identifies your Okta IDP (Okta's Entity ID)."
                />
            </Flexer>
            <Flexer>
                <Input
                    label="X.509 Certificate:"
                    multiline={{ rows: 3, resize: 'vertical' }}
                    value={draft.certificate}
                    error={form.errors.certificate}
                    onChange={x => patchDraft({ certificate: x })}
                    hint="The certificate your Okta IDP uses to sign assertions."
                />
            </Flexer>
            <Flexer>
                <ResultInfoForMutation mutation={mutation} onSuccess="Settings saved successfully." />
            </Flexer>
            {!mutation.isSuccess && <Flexer flex="row" justify="end">
                <Button disabled={mutation.isLoading || !draftDirty} onAction={onSave}>
                    Save
                </Button>
            </Flexer>}
            {mutation.isSuccess && <>
                <Flexer flex="row" justify="end">
                    <Button variant="primary" onAction={() => gotoStep(3)}>
                        Next
                    </Button>
                </Flexer>
            </>}
        </SetupStep>
    );
};

export const SetupOktaSingleSignOnView = React.memo(() => {

    const nav = useResolve(AppNavigator);
    const [activeStep, setActiveStep] = useState(0);
    const steps = 4; // 4 steps for Okta

    const form = useSingleSignOnForm();
    const mutation = useSingleSignOnMutation();

    const gotoStep = (step: number | 'end') => {
        if (step === 'end') {
            nav.go('~/manage/sso');
        }
        else if (step >= 0 && step < steps) {
            setActiveStep(step);
        }
    };

    const stepProps: StepProps = {
        form,
        mutation,
        currentStep: activeStep,
        gotoStep,
    };

    return <Flexer flex="column" gap="2" maxWidth="54rem">
        <Title size="lg" color={Colors.text}>Okta Setup</Title>
        <SetupStepViewer steps={steps} activeIndex={activeStep} onStep={gotoStep} />
        {activeStep === 0 && <CreateAppStep {...stepProps} />}
        {activeStep === 1 && <ConfigureServiceProviderStep {...stepProps} />}
        {activeStep === 2 && <ConfigureIdentityProviderStep {...stepProps} />}
        {activeStep === 3 && <CommonFinalStep {...stepProps} />}
    </Flexer>;
});
